// src/pages/portfolio/PortfolioPage.js
import { useState } from 'react';
import { useQuery } from 'react-query';
import { fetchPortfolio, fetchCategories } from '../../services/api';
import { useLang } from '../../i18n/LanguageContext';
import { SectionHeader, CategoryFilter, SkeletonGrid, ErrorBox, EmptyState } from '../../components/ui';
import PortfolioCard from '../../components/ui/PortfolioCard';
import styles from './PortfolioPage.module.css';

export default function PortfolioPage() {
  const { ui, lang } = useLang();
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: categories = [] } = useQuery('categories', fetchCategories);

  const params = {
    page,
    ...(activeCategory && { 'category__slug': activeCategory }),
    ...(search && { search }),
  };
  const { data, isLoading, isError, refetch } = useQuery(
    ['portfolio', params],
    () => fetchPortfolio(params),
    { keepPreviousData: true }
  );

  const items = data?.results || data || [];
  const totalPages = data?.count ? Math.ceil(data.count / 12) : 1;

  const handleCategory = (slug) => { setActiveCategory(slug); setPage(1); };
  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  return (
    <main className={styles.main}>
      <div className={styles.header}>
        <SectionHeader
          badge={ui('✦ نمونه‌کارها', '✦ Portfolio')}
          title={ui('گالری کامل شمع‌های کیوت کندل', 'Full Cute Candels Gallery')}
          sub={ui('برای سفارش با ما در تماس باشید', 'Contact us to place an order')}
        />

        {/* Search */}
        <div className={styles.searchWrap}>
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder={ui('جستجو در نمونه‌کارها...', 'Search portfolio...')}
            className={styles.search}
          />
          {search && (
            <button className={styles.clearBtn} onClick={() => { setSearch(''); setPage(1); }}>✕</button>
          )}
        </div>

        {/* Category filters */}
        <CategoryFilter
          categories={categories}
          active={activeCategory}
          onSelect={handleCategory}
          lang={lang}
        />
      </div>

      {/* Grid */}
      {isLoading && <SkeletonGrid count={9} />}
      {isError && <ErrorBox onRetry={refetch} />}
      {!isLoading && !isError && items.length === 0 && (
        <EmptyState icon="🕯️" fa="نمونه‌کاری یافت نشد" en="No portfolio items found" lang={lang} />
      )}
      {!isLoading && !isError && items.length > 0 && (
        <div className={styles.grid}>
          {items.map((item) => <PortfolioCard key={item.id} item={item} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className="btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            {ui('قبلی', 'Prev')}
          </button>
          <span className={styles.pageInfo}>{page} / {totalPages}</span>
          <button
            className="btn-secondary"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            {ui('بعدی', 'Next')}
          </button>
        </div>
      )}
    </main>
  );
}
